import * as React from 'react';
import { get, head, isEmpty } from 'lodash';
import '../../renewal/style.scss';
import { ICustomerPaymentMethod, ITraveller } from '../../../../../../data/api/models';
import { IQuoteActions } from '../../../../../../services/quote/models';
import { IAuthActions } from '../../../../../../services/auth/reducer';
import { IBrandingActions } from '../../../../../../services/branding/models';
import { differenceInDays, format, parse, subDays } from 'date-fns';
import Loading from '../../../../../../components/Loading';
import PaymentWithCardModal from '../../../../../../components/PaymentWithCard/components/PaymentWithCardModal';
import PaymentWithCardTransactionModal
  from '../../../../../../components/PaymentWithCard/components/PaymentWithCardTransactionModal';
import Heap from '../../../../../../helpers/Heap';

export interface IRenewNow {
  startDate: string;
  assignedPaymentMethod: ICustomerPaymentMethod;
  quoteReference: string;
  region: string;
  travellers: ITraveller[];
  policyId: string;
  customerId: string;
  dispatch: any;
  customerPaymentMethods: ICustomerPaymentMethod[];
  actions: {
    auth: IAuthActions;
    branding: IBrandingActions;
    quote: IQuoteActions;
    calculate: any;
    push(path: string);
    destroy(formName: string);
    change: (form, field, value) => void;
  };

  hidePurchaseCardButton: (hide: boolean) => void;
}

interface IPaymentError {
  eventName: string;
  paymentMethodId: string;
  errorMessage: string;
}

interface IRenewNowStates {
  scenario: number;
  loading: boolean;
  showCardModal: boolean;
  showCardPaymentTransactionModal: boolean;
  transactionId: string;
}

export const SEVEN_DAYS_ADVANCED = 7;

export class RenewNow extends React.Component<IRenewNow, IRenewNowStates> {

  constructor(props) {
    super(props);
    this.state = {
      scenario: 0,
      loading: true,
      showCardModal: false,
      showCardPaymentTransactionModal: false,
      transactionId: ''
    }
  }

  componentDidMount() {
    const { actions, quoteReference } = this.props;
    Promise.resolve(actions.quote.getPaymentFailure(quoteReference)).then((data: IPaymentError) => {
      this.setScenarios(get(this.props, 'assignedPaymentMethod'), data)
      this.setState({ loading: false });
    }).catch((error) => {
      this.setScenarios(get(this.props, 'assignedPaymentMethod'))
      this.setState({ loading: false });
    })
  }

  private setScenarios = (assignedPaymentMethod: ICustomerPaymentMethod, paymentError: IPaymentError = null) => {
    const { startDate } = this.props;
    if (assignedPaymentMethod) {
      const statuses = ['expired', 'disabled']
      const cardHasExpired = statuses.includes(get(assignedPaymentMethod, 'status', ''));
      const daysDifference = differenceInDays(parse(startDate, 'dd/MM/yyyy', new Date()), new Date());

      if (!cardHasExpired && !isEmpty(paymentError) && assignedPaymentMethod.id === get(paymentError, 'paymentMethodId')) {
        this.setState({ scenario: 2 })
        return;
      }

      if (!cardHasExpired && daysDifference > SEVEN_DAYS_ADVANCED) {
        this.setState({ scenario: 1 })
        return;
      }

      if (!cardHasExpired && daysDifference < SEVEN_DAYS_ADVANCED) {
        this.setState({ scenario: 4 })
        return;
      }

      if (cardHasExpired) {
        this.setState({ scenario: 3 })
        return;
      }

    } else {
      this.setState({ scenario: 3 })
      return;
    }

  }

  private handlePayment = () => {
    const { hidePurchaseCardButton, quoteReference, region } = this.props;
    this.setState({
      showCardModal: true,
    });
    Heap.track('PayEarly', { 'Renewal Quote Reference': quoteReference, 'Region': region })
    hidePurchaseCardButton(true)
  }
  private renderScenario = (scenario: number) => {
    const { startDate } = this.props;
    const sevenDaysAdvance = format(subDays(parse(startDate, 'dd/MM/yyyy', new Date()), SEVEN_DAYS_ADVANCED), 'dd/MM/yyyy')
    switch (scenario) {
      case 1:
        return (
          <>
            <div>Your policy will <b>automatically renew</b> on {startDate} and we will take payment from the card on
              file 7 days before your renewal date to ensure you don't lose continuous cover.
            </div>
            <div className={'mt-15'}>Want to pay now? Click <span className={'payment-link'}
                                                                  onClick={this.handlePayment}>here</span></div>
          </>
        )

      case 2:
        return (
          <>
            <div>We are <b>unable to automatically renew</b> your policy on {sevenDaysAdvance} as there is an issue with
              your card on file.
            </div>
            <div>Please update your card details to ensure your policy renews using the buttons provided</div>
          </>
        )

      case 3:
        return (
          <>
            <div>We are <b>unable to automatically renew</b> your policy on {sevenDaysAdvance} as we don't hold a card
              on file for you.
            </div>
            <div>Please add your card details to ensure your policy renews</div>
          </>
        )

      case 4:
        return (
          <>
            <div>Your policy will <b>automatically renew</b> on {startDate} and we will take payment from the card on
              your renewal date.
            </div>
            <div className={'mt-15'}>Want to pay now? Click <span className={'payment-link'}
                                                                  onClick={this.handlePayment}>here</span>
            </div>
          </>
        )

      default:
        return null;
    }
  }

  private closeCardModal = () => {
    const { hidePurchaseCardButton } = this.props;
    this.setState({
      showCardModal: false,
    });
    hidePurchaseCardButton(false)
  }

  private openCardTransactionModal = (transactionId: string) => {
    const { hidePurchaseCardButton } = this.props;
    this.setState({
      showCardPaymentTransactionModal: true,
      transactionId,
    });
    hidePurchaseCardButton(true)
  }

  private closeCardTransactionModal = () => {
    const { hidePurchaseCardButton } = this.props;
    this.setState({
      showCardPaymentTransactionModal: false,
    });
    hidePurchaseCardButton(true)
  }

  private completePayment = (data: any) => {
    const { actions, quoteReference, region } = this.props;
    Heap.track('PayEarlyComplete', {
      'Renewal Quote Reference': quoteReference,
      'Region': region,
      'Policy Number': get(head(data), 'policy_number', '')
    })
    actions.push(`/account/summary`);
  }

  render() {
    const { travellers, policyId, customerId, dispatch, customerPaymentMethods } = this.props;
    const { scenario, loading, showCardModal, showCardPaymentTransactionModal, transactionId } = this.state;
    if (loading) {
      return (
        <Loading/>
      )
    }

    return (
      <>
        <div>{this.renderScenario(scenario)}</div>
        {showCardModal && (
          <PaymentWithCardModal
            show={showCardModal}
            changeRenewalType={false}
            closeModal={this.closeCardModal}
            openCardTransactionModal={this.openCardTransactionModal}
            leadTraveller={travellers[0]}
            paymentMethods={customerPaymentMethods}
            travellers={travellers}
            customerId={customerId}
            policyId={policyId}
          />
        )}

        {showCardPaymentTransactionModal && (
          <PaymentWithCardTransactionModal
            show={showCardPaymentTransactionModal}
            closeModal={this.closeCardTransactionModal}
            transactionId={transactionId}
            policyId={policyId}
            completePayment={this.completePayment}
            dispatch={dispatch}
          />
        )}
      </>
    )
  }
}
